import Phaser from 'phaser';
import BaseGameScene from './BaseGameScene.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants.js';
import { logStage, STAGES } from '../utils/IPLogger.js';

/**
 * GameScene - Level 1: Rescue the Scientists
 *
 * Mission: Rescue 3 scientists trapped in the city
 * Map: 1600x1200 city grid with random buildings
 */
export default class GameScene extends BaseGameScene {
  constructor() {
    super({ key: 'GameScene' });
  }

  // ==================== LEVEL CONFIGURATION ====================

  getLevelConfig() {
    return {
      width: GAME_WIDTH * 2,   // 1600
      height: GAME_HEIGHT * 2, // 1200
      initialZombies: 10,
      zombieSpawnDistance: 200,
    };
  }

  getNextLevelScene() {
    return 'Level2CutsceneScene';
  }

  // ==================== LEVEL-SPECIFIC INIT ====================

  init() {
    super.init();

    // Level 1 specific state
    this.scientists = null;
    this.scientistsRescued = 0;
    this.totalScientists = 3;
  }

  // ==================== LEVEL-SPECIFIC CREATE ====================

  create() {
    super.create();

    // Log arrival at level 1
    logStage(STAGES.LEVEL_1_START);

    // Create scientist group (rescue targets)
    this.scientists = this.physics.add.group();
    this.spawnScientists(3);

    // Set up scientist rescue collision
    this.physics.add.overlap(this.player, this.scientists, this.rescueScientist, null, this);
  }

  // ==================== ANIMATIONS ====================

  createAnimations() {
    // Call parent to create zombie animations
    super.createAnimations();

    // Hero animations from hero_sheet (hero_0.png)
    if (!this.anims.exists('hero_idle_left')) {
      this.anims.create({
        key: 'hero_idle_left',
        frames: [{ key: 'hero_sheet', frame: 0 }],
        frameRate: 1,
      });

      this.anims.create({
        key: 'hero_idle_up',
        frames: [{ key: 'hero_sheet', frame: 1 }],
        frameRate: 1,
      });

      this.anims.create({
        key: 'hero_aim',
        frames: [{ key: 'hero_sheet', frame: 2 }],
        frameRate: 1,
      });

      this.anims.create({
        key: 'hero_shoot',
        frames: [{ key: 'hero_sheet', frame: 3 }],
        frameRate: 8,
      });

      // Hero actions from hero_actions (hero_1.png)
      this.anims.create({
        key: 'hero_hit',
        frames: [{ key: 'hero_actions', frame: 2 }],
        frameRate: 1,
      });

      this.anims.create({
        key: 'hero_dead',
        frames: [{ key: 'hero_actions', frame: 3 }],
        frameRate: 1,
      });

      this.anims.create({
        key: 'hero_aim_right',
        frames: [{ key: 'hero_actions', frame: 1 }],
        frameRate: 1,
      });
    }
  }

  // ==================== BACKGROUND/MAP ====================

  createBackground() {
    const config = this.getLevelConfig();
    const TILE_SIZE = 32;
    const mapWidth = Math.ceil(config.width / TILE_SIZE);
    const mapHeight = Math.ceil(config.height / TILE_SIZE);

    // Fill the background with 192px road tiles (bottom layer)
    const ROAD_TILE_SIZE = 192;
    const roadCols = Math.ceil(config.width / ROAD_TILE_SIZE);
    const roadRows = Math.ceil(config.height / ROAD_TILE_SIZE);

    for (let r = 0; r < roadRows; r++) {
      for (let c = 0; c < roadCols; c++) {
        const x = c * ROAD_TILE_SIZE;
        const y = r * ROAD_TILE_SIZE;
        const frame = Phaser.Math.Between(0, 17);
        const road = this.add.image(x, y, 'road_tiles_clean', frame);
        road.setOrigin(0, 0);
        road.setDepth(-100);
      }
    }

    // Create a blank tilemap for sidewalks
    const map = this.make.tilemap({
      tileWidth: TILE_SIZE,
      tileHeight: TILE_SIZE,
      width: mapWidth,
      height: mapHeight,
    });

    const sidewalkTiles = map.addTilesetImage('tile_sidewalk', 'tile_sidewalk', 32, 32);
    const sidewalkLayer = map.createBlankLayer('Sidewalk', sidewalkTiles);
    sidewalkLayer.setDepth(-90);

    // Generate City Grid
    const BLOCK_SIZE = 12;
    const ROAD_WIDTH = 2;

    for (let x = 0; x < mapWidth; x++) {
      for (let y = 0; y < mapHeight; y++) {
        const isRoadX = (x % BLOCK_SIZE) < ROAD_WIDTH;
        const isRoadY = (y % BLOCK_SIZE) < ROAD_WIDTH;

        if (isRoadX || isRoadY) {
          // Road area - leave empty
        } else {
          const isSidewalkX = ((x % BLOCK_SIZE) === ROAD_WIDTH) || ((x % BLOCK_SIZE) === BLOCK_SIZE - 1);
          const isSidewalkY = ((y % BLOCK_SIZE) === ROAD_WIDTH) || ((y % BLOCK_SIZE) === BLOCK_SIZE - 1);

          if (isSidewalkX || isSidewalkY) {
            sidewalkLayer.putTileAt(Phaser.Math.Between(0, 0), x, y);
          } else {
            // Building interior area - place building sprite
            if ((x % BLOCK_SIZE) === ROAD_WIDTH + 1 && (y % BLOCK_SIZE) === ROAD_WIDTH + 1) {
              const buildingX = x * TILE_SIZE + 128;
              const buildingY = y * TILE_SIZE + 128;

              const isometricBuildings = [
                'building_apartment',
                'building_destroyed_skyscraper',
                'building_grocery_store',
                'building_hospital',
                'building_hotel',
                'building_parking_garage',
                'building_school',
                'building_subway_entrance'
              ];

              const buildingKey = isometricBuildings[Phaser.Math.Between(0, isometricBuildings.length - 1)];
              const building = this.add.image(buildingX, buildingY, buildingKey);
              building.setScale(0.7);
              building.setOrigin(0.5, 0.5);
              building.setDepth(-80);
            }
          }
        }
      }
    }

    // Place props - Vehicles on roads
    const vehicleKeys = ['vehicle_taxi', 'vehicle_police_0', 'vehicle_police_1', 'vehicle_van'];
    for (let i = 0; i < 20; i++) {
      const x = Phaser.Math.Between(0, mapWidth - 1) * TILE_SIZE;
      const y = Phaser.Math.Between(0, mapHeight - 1) * TILE_SIZE;

      const tx = Math.floor(x / TILE_SIZE);
      const ty = Math.floor(y / TILE_SIZE);
      const isRoad = (tx % BLOCK_SIZE) < ROAD_WIDTH || (ty % BLOCK_SIZE) < ROAD_WIDTH;

      if (isRoad) {
        const vehicleKey = vehicleKeys[Phaser.Math.Between(0, vehicleKeys.length - 1)];
        const vehicle = this.add.image(x + 16, y + 16, vehicleKey);
        vehicle.setScale(0.3);
        vehicle.setRotation(Math.random() * Math.PI * 2);
        vehicle.setDepth(-50);
      }
    }

    // Debris on sidewalks
    const debrisKeys = ['debris_dumpster_blue', 'debris_dumpster_yellow', 'debris_trashcan_0', 'debris_hydrant_0', 'debris_mailbox_0', 'debris_newsstand_0'];
    for (let i = 0; i < 30; i++) {
      const x = Phaser.Math.Between(0, mapWidth - 1) * TILE_SIZE;
      const y = Phaser.Math.Between(0, mapHeight - 1) * TILE_SIZE;
      const tx = Math.floor(x / TILE_SIZE);
      const ty = Math.floor(y / TILE_SIZE);
      const isRoad = (tx % BLOCK_SIZE) < ROAD_WIDTH || (ty % BLOCK_SIZE) < ROAD_WIDTH;

      if (!isRoad) {
        const debrisKey = debrisKeys[Phaser.Math.Between(0, debrisKeys.length - 1)];
        const debris = this.add.image(x + 16, y + 16, debrisKey);
        debris.setDepth(-40);
        debris.setScale(0.4);
      }
    }

    // Barricades
    const barricadeKeys = ['barricade_sandbags_0', 'barricade_sandbags_1', 'barricade_chair_0', 'barricade_concrete_0', 'barricade_fence_0', 'barricade_drum_0'];
    for (let i = 0; i < 10; i++) {
      const x = Phaser.Math.Between(0, mapWidth - 1) * TILE_SIZE;
      const y = Phaser.Math.Between(0, mapHeight - 1) * TILE_SIZE;
      const tx = Math.floor(x / TILE_SIZE);
      const ty = Math.floor(y / TILE_SIZE);
      const isRoad = (tx % BLOCK_SIZE) < ROAD_WIDTH || (ty % BLOCK_SIZE) < ROAD_WIDTH;

      if (isRoad) {
        const barricadeKey = barricadeKeys[Phaser.Math.Between(0, barricadeKeys.length - 1)];
        const barricade = this.add.image(x + 16, y + 16, barricadeKey);
        barricade.setScale(0.4);
        barricade.setDepth(-45);
      }
    }
  }

  // ==================== HUD ====================

  createHUD() {
    // Health bar
    this.healthText = this.add.text(16, 16, 'HEALTH: 100', {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#00ff00',
    });
    this.healthText.setScrollFactor(0);

    // Score
    this.scoreText = this.add.text(16, 40, 'SCORE: 0', {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#ffff00',
    });
    this.scoreText.setScrollFactor(0);

    // Scientists rescued
    this.rescueText = this.add.text(16, 64, `RESCUED: 0/${this.totalScientists}`, {
      fontFamily: 'Courier New',
      fontSize: '18px',
      fill: '#00ffff',
    });
    this.rescueText.setScrollFactor(0);

    // Mission objective
    this.missionText = this.add.text(GAME_WIDTH / 2, 16, 'MISSION: RESCUE THE SCIENTISTS', {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#ff00ff',
    });
    this.missionText.setOrigin(0.5, 0);
    this.missionText.setScrollFactor(0);

    // Weapon mode indicator
    this.shellIcon = this.add.image(32, GAME_HEIGHT - 32, 'shell_icon');
    this.shellIcon.setScrollFactor(0);
    this.shellIcon.setScale(1.5);

    this.rocketIcon = this.add.image(80, GAME_HEIGHT - 32, 'rocket_icon');
    this.rocketIcon.setScrollFactor(0);
    this.rocketIcon.setScale(1.5);

    this.weaponSelector = this.add.rectangle(32, GAME_HEIGHT - 32, 40, 40);
    this.weaponSelector.setStrokeStyle(3, 0xff0000);
    this.weaponSelector.setFillStyle(0x000000, 0);
    this.weaponSelector.setScrollFactor(0);

    this.rocketCountText = this.add.text(80, GAME_HEIGHT - 50, `x${this.homingRocketsRemaining}`, {
      fontFamily: 'Courier New',
      fontSize: '14px',
      fill: '#ff00ff',
    });
    this.rocketCountText.setScrollFactor(0);

    // Controls hint
    this.controlsText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 16,
      'SPACE: Fire | 2: Switch Weapon | 0: Airstrike', {
      fontFamily: 'Courier New',
      fontSize: '12px',
      fill: '#888888',
    });
    this.controlsText.setOrigin(0.5);
    this.controlsText.setScrollFactor(0);

    // Initialize weapon display
    this.updateWeaponModeDisplay();
  }

  updateHUD() {
    const healthColor = this.player.health > 50 ? '#00ff00' :
                        this.player.health > 25 ? '#ffff00' : '#ff0000';
    this.healthText.setText(`HEALTH: ${this.player.health}`);
    this.healthText.setFill(healthColor);

    this.scoreText.setText(`SCORE: ${this.score}`);
    this.rescueText.setText(`RESCUED: ${this.scientistsRescued}/${this.totalScientists}`);
  }

  // ==================== SCIENTIST SYSTEM ====================

  spawnScientists(count) {
    const config = this.getLevelConfig();

    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(200, config.width - 200);
      const y = Phaser.Math.Between(200, config.height - 200);

      // Don't spawn too close to player
      const distToPlayer = Phaser.Math.Distance.Between(x, y, this.player.x, this.player.y);
      if (distToPlayer < 200) {
        i--;
        continue;
      }

      const scientist = this.scientists.create(x, y, 'scientist_sprite');
      scientist.setCollideWorldBounds(true);
      scientist.setScale(0.12);

      const bodyWidth = scientist.displayWidth * 0.8;
      const bodyHeight = scientist.displayHeight * 0.8;
      scientist.body.setSize(bodyWidth, bodyHeight);
      scientist.body.setOffset(
        (scientist.width - bodyWidth) / 2,
        (scientist.height - bodyHeight) / 2
      );

      scientist.body.setImmovable(true);
    }
  }

  rescueScientist(player, scientist) {
    if (scientist.isBeingRescued) return;
    scientist.isBeingRescued = true;

    scientist.body.enable = false;

    this.scientistsRescued++;
    this.score += 500;

    if (this.sound.get('rescue')) {
      this.sound.play('rescue', { volume: 0.5 });
    }

    scientist.setTint(0x00ff00);

    const originalScale = scientist.scale;

    this.tweens.add({
      targets: scientist,
      alpha: 0,
      scale: originalScale * 0.5,
      duration: 500,
      onComplete: () => {
        if (scientist && scientist.active) {
          scientist.destroy();
        }

        if (this.scientistsRescued >= this.totalScientists) {
          this.levelComplete();
        }
      },
    });
  }

  // ==================== LEVEL TRANSITIONS ====================

  levelComplete() {
    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 2000,
      });
    }

    // Victory text
    const victoryText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'MISSION COMPLETE!', {
      fontFamily: 'Courier New',
      fontSize: '48px',
      fill: '#00ff00',
    });
    victoryText.setOrigin(0.5);
    victoryText.setScrollFactor(0);

    this.time.delayedCall(3000, () => {
      if (this.music) this.music.stop();
      this.scene.start('Level2CutsceneScene');
    });
  }

  gameOver() {
    // Log death at level 1
    logStage(STAGES.LEVEL_1_DEATH);

    // Fade out music
    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 2000,
      });
    }

    // Tank destruction effect
    this.player.setTint(0xff0000);
    this.tweens.add({
      targets: this.player,
      rotation: this.player.rotation + Math.PI * 4,
      scale: 0.05,
      alpha: 0.3,
      duration: 1000,
    });

    const gameOverText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'GAME OVER', {
      fontFamily: 'Courier New',
      fontSize: '64px',
      fill: '#ff0000',
    });
    gameOverText.setOrigin(0.5);
    gameOverText.setScrollFactor(0);

    this.physics.pause();

    this.time.delayedCall(3000, () => {
      if (this.music) this.music.stop();
      this.scene.start('MenuScene');
    });
  }
}
